import { LISTED_TAG_CHIP, TAG_CHIP_BORDER_WIDTH } from './TagChipMetrics';

/** A listed chip's label font, as computed on a rendered label. */
export interface ITagRowFont {
    fontStyle: string;
    fontWeight: string;
    fontSize: string;
    fontFamily: string;
}

/**
 * The outer width of a listed tag chip, or of the "+N more" chip beside them, from its label's text,
 * so a row can tell which chips fit without rendering and measuring them.
 */
export class TagChipWidths {
    private context?: CanvasRenderingContext2D;
    private textWidths = new Map<string, number>();

    listedTag = ({ tag, font }: { tag: string; font: ITagRowFont; }): number => {
        return this.measureLabel({ text: tag, font }) + 2 * (LISTED_TAG_CHIP.paddingInline + TAG_CHIP_BORDER_WIDTH + LISTED_TAG_CHIP.marginInline);
    };

    // the "+N more" chip has no border
    moreChip = ({ label, font }: { label: string; font: ITagRowFont; }): number => {
        return this.measureLabel({ text: label, font }) + 2 * (LISTED_TAG_CHIP.paddingInline + LISTED_TAG_CHIP.marginInline);
    };

    private measureLabel = ({ text, font }: { text: string; font: ITagRowFont; }): number => {
        const canvasFont = `${font.fontStyle} ${font.fontWeight} ${font.fontSize} ${font.fontFamily}`;
        const key = `${canvasFont}\n${text}`;
        const cached = this.textWidths.get(key);
        if (cached !== undefined) {
            return cached;
        }

        const context = this.getContext();
        context.font = canvasFont;
        const width = context.measureText(text).width;
        // while a font loads, the canvas measures its fallback, which must not stand in for it later
        if (document.fonts.status === 'loaded') {
            this.textWidths.set(key, width);
        }
        return width;
    };

    private getContext = (): CanvasRenderingContext2D => {
        if (!this.context) {
            const context = document.createElement('canvas').getContext('2d');
            if (!context) {
                throw new Error('TagChipWidths could not get a 2d canvas context to measure tag labels');
            }
            this.context = context;
        }
        return this.context;
    };
}
